import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from "react-router-dom";
import Restricted from '../../../contexts/PermissionProvider/RestrictedWrapper';
import { useTable } from '../../../contexts/table/TableContext';
import deleteIcon from '../../../images/Delete.svg';
import editIcon from '../../../images/Edit.svg';
import MusteringPointIcon from "../../../images/Mustering Point.svg";
import swapIcon from '../../../images/Swap.png';
import downloadIcon from '../../../images/download_button_color.svg';
import { TableColumn } from '../../../models/table/TableColumn';
import TableCell from '../table-cell/table-cell';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from '../../Tooltip/Tooltip';
import { CellWarning } from '../../../models/CustomRowAction';
import { useLocale } from '../../../contexts/localeContext';
import { isQuickEnrollEvent } from '../../../constants/event-types';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';

type Props = {
    rowIndex: number,
    columnDefinitions: TableColumn[],
    rowData: any[],
    isSelected?: boolean,
    onSelect?: () => void,
    onRowClicked?: (rowData: any) => void,
    isRowHighlighted?: boolean,
    tableId?: string,
    customRowColourWithRange?: any,
    showCustomRowColour?: boolean,
    rowPath?: any,
    isStriped?: boolean,
    testId?: number
}

const getRouteNameFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[2];
}

const TableRow: FC<Props> = ({ rowIndex, columnDefinitions, rowData, isSelected = undefined, onSelect = () => { }, onRowClicked = undefined, isRowHighlighted, tableId, customRowColourWithRange, showCustomRowColour, rowPath, isStriped = true, testId }) => {
    const [rowHovered, setRowHovered] = useState(false);
    const routeName = useSelector((state: RouterRootState) => getRouteNameFromPath(state.router.location.pathname));
    const history = useHistory();
    const [isCheckBoxClicked, setCheckBoxClicked] = useState<boolean>(false);
    const tableContext = useTable();
    const [showDeleteIcon, setShowDeleteIcon] = useState<boolean>(false);
    const [showEditIcon, setShowEditIcon] = useState<boolean>(false);
    const [showDownloadIcon, setShowDownloadIcon] = useState<boolean>(false);
    const [showMusteringIcon, setShowMusteringIcon] = useState<boolean>(false);
    const showQuickEnroll = (isQuickEnrollEvent(rowData['eventId'], rowData['credentialHolder']));
    const { translate } = useLocale();

    useEffect(() => {
        if (columnDefinitions && rowData) {
            const newRowData: any[] = [];
            columnDefinitions.forEach((item) => {
                if (item.active === true) {
                    newRowData[item.accessor] = rowData[item.accessor];
                }
            });
        }
    }, [columnDefinitions, rowData]);

    useEffect(() => {
        if (isRowHighlighted) {
            if (onRowClicked) {
                onRowClicked(rowData)
            }
        }

        setShowDeleteIcon(showDelete());
        setShowDownloadIcon(showDownload());
        setShowEditIcon(showEdit());
        setShowMusteringIcon(showMustering());
    }, [])

    useEffect(() => {
        if (isCheckBoxClicked) {
            if (onSelect) {
                onSelect();
            }
        }
    }, [isCheckBoxClicked])

    const rowClicked = (e) => {
        if (isCheckBoxClicked == false) {
            const query = tableId ? `table[id='${tableId}'] tr.bg-portal-blue-primary.bg-opacity-50` : 'tr.bg-portal-blue-primary.bg-opacity-50';
            const listOfTR: NodeListOf<Element> = document.querySelectorAll<Element>(query);

            listOfTR.forEach(element => {
                if (element.className.includes('bg-portal-blue-primary bg-opacity-50'))
                    element.className = ""
            });

            if (rowPath) {
                history.push(`${rowPath}${rowData["id"]}`)
            }
            else if (onRowClicked) {
                const clickedRow = e.target.closest('tr');
                clickedRow.className = "relative bg-portal-blue-primary bg-opacity-50";
                onRowClicked(rowData)
            }
            else if (showEditIcon && tableContext.editItem) {
                tableContext.editItem(rowData['id']);
            }
        }
        setCheckBoxClicked(false);
    }

    const isRowHovered = (e, hovered) => {
        setRowHovered(hovered)
        const row: Element = e.target.closest('tr');

        hovered
            ? row.className = row.className + 'bg-portal-blue-hover bg-opacity-50 cursor-pointer'
            : row.className = row.className.replace('bg-portal-blue-hover bg-opacity-50 cursor-pointer', '')

        if (!showQuickEnroll) {
            hovered
                ? row.className = row.className + 'bg-portal-blue-hover bg-opacity-50'
                : row.className = row.className.replace('bg-portal-blue-hover bg-opacity-50', '');
        }
    }

    const onDeleteMethod = (_: any) => {
        if (!!!tableContext.deleteItem) {
            return tableContext.deleteItem;
        }

        let id = rowData['id'];
        let isCustomDeleteKey = false;

        if (tableContext.customDeleteKey && rowData[tableContext.customDeleteKey.idKey]) {
            id = rowData[tableContext.customDeleteKey.idKey];
            isCustomDeleteKey = true;
        }

        return tableContext.deleteItem(id, isCustomDeleteKey);
    }

    const showDelete = (): boolean => {
        if (tableContext.customDelete && tableContext.customDeleteWithRange) {
            return isDeleteEnabled('customDelete') && isDeleteEnabled('customDeleteWithRange');
        }
        else if (tableContext.customDelete) {
            return isDeleteEnabled('customDelete');
        }
        else if (tableContext.customDeleteWithRange) {
            return isDeleteEnabled('customDeleteWithRange');
        }

        return tableContext.deleteItem !== undefined;
    };

    const isDeleteEnabled = (type: 'customDelete' | 'customDeleteWithRange') => {
        if (type === 'customDelete' && tableContext.customDelete) {
            return rowData[tableContext.customDelete.fieldName] === tableContext.customDelete.comparisonValue;
        }
        else if (type === 'customDeleteWithRange' && tableContext.customDeleteWithRange) {
            const itemValue = rowData[tableContext.customDeleteWithRange.fieldName] as never;
            return !tableContext.customDeleteWithRange.excludedValues.includes(itemValue);
        }
        return false;
    }

    const showEdit = () => {
        if (tableContext.customEdit) {
            return rowData[tableContext.customEdit.fieldName] === tableContext.customEdit.comparisonValue
        }
        else if (tableContext.customEditWithRange) {
            const value = rowData[tableContext.customEditWithRange?.fieldName] as never;
            return !tableContext.customEditWithRange?.excludedValues.includes(value)
        }

        return tableContext.editItem !== undefined;
    }

    const showDownload = () => {
        if (tableContext.customDownload) {
            return rowData[tableContext.customDownload.fieldName] === tableContext.customDownload.comparisonValue
        }

        return tableContext.downloadItem !== undefined;
    }

    const showMustering = () => {
        if (tableContext.customMusteringWithRange) {
            const value = rowData[tableContext.customMusteringWithRange.fieldName] as never;
            return !tableContext.customMusteringWithRange.excludedValues.includes(value)
        }

        return tableContext.manuallyMuster !== undefined;
    }

    const getIconJSX = (iconType: "EDIT" | "DELETE" | "SWAP" | "DOWNLOAD" | "MUSTERING", className: string = ""): JSX.Element => {
        switch (iconType) {
            case "EDIT":
                return <Restricted to={`update.${routeName}.element`}>
                    <button
                        id='editRecordButton'
                        onMouseDown={() => { tableContext.editItem && tableContext.editItem(rowData['id']) }}
                        className={className} >
                        <img
                            src={editIcon}
                            style={{ width: "18px" }}
                            alt="Edit Icon" />
                    </button>
                </Restricted>;
            case "DELETE":
                return <Restricted to={`delete.${routeName}.element`}>
                    <button
                        data-cy="deleteRecord"
                        id='deleteRecordButton'
                        onMouseDown={onDeleteMethod}
                        className={className}>
                        <img
                            id='deleteRecordButton'
                            src={deleteIcon}
                            style={{ width: "18px" }}
                            alt="Delete Icon" />
                    </button>
                </Restricted>;
            case "SWAP":
                return <button
                    id='swapButton'
                    onMouseDown={() => { tableContext.swapItem && tableContext.swapItem(rowData) }}
                    className={className}>
                    <img
                        src={swapIcon}
                        style={{ width: "18px" }}
                        alt="Swap Icon" />
                </button>;
            case "DOWNLOAD":
                return <Restricted to={`update.${routeName}.element`}>
                    <button
                        id="downloadButton"
                        onMouseDown={() => tableContext.downloadItem && tableContext.downloadItem(rowData)}
                        className={className}>
                        <img
                            src={downloadIcon}
                            style={{ width: "18px" }}
                            alt="Download Icon" />
                    </button>
                </Restricted>;
            case "MUSTERING":
                return <button
                    onMouseDown={() => tableContext.manuallyMuster && tableContext.manuallyMuster(rowData)}
                    className={className}>
                    <img
                        src={MusteringPointIcon}
                        style={{ width: "18px" }}
                        alt="Mustering Icon" />
                </button>;
        }
    }

    const getRowActionsJSX = (): JSX.Element => {
        if (!!tableContext.swapItem && showDeleteIcon) {
            return <div className="absolute inset-y-0 right-0 flex flex-row justify-center mr-1 -ml-6">
                {getIconJSX("SWAP", "mr-2 -ml-6")}
                {getIconJSX("DELETE")}
            </div>
        }
        else if (!!tableContext.swapItem && showEditIcon) {
            return <div className="absolute inset-y-0 right-0 flex flex-row justify-between mr-1 -ml-6">
                {getIconJSX("EDIT", "-ml-6")}
                {getIconJSX("SWAP")}
            </div>
        }
        else if (showEditIcon && showDeleteIcon) {
            return <div className="absolute inset-y-0 right-0 flex flex-row justify-between mr-1 -ml-6">
                {getIconJSX("EDIT", "mr-2 -ml-6")}
                {getIconJSX("DELETE")}
            </div>
        }
        else if (showDownloadIcon && showDeleteIcon) {
            return <div className="absolute inset-y-0 right-0 flex flex-row justify-between mr-1 -ml-6">
                {getIconJSX("DOWNLOAD", "mr-2 -ml-6")}
                {getIconJSX("DELETE")}
            </div>
        }
        else if (showDeleteIcon) {
            return <div className="absolute inset-y-0 right-0 flex flex-row justify-between mr-1 -ml-6">
                {getIconJSX("DELETE")}
            </div>
        }
        else if (showEditIcon) {
            return <div className="absolute inset-y-0 right-0 flex flex-row justify-between mr-1 -ml-6">
                {getIconJSX("EDIT")}
            </div>
        }
        else if (showMusteringIcon) {
            return <div className="absolute inset-y-0 right-0 flex flex-row justify-between mr-1 -ml-6">
                {getIconJSX("MUSTERING")}
            </div>
        }

        return <></>;
    }

    const handleMoveUp = (event: React.MouseEvent<HTMLButtonElement>) => {
        tableContext.onMoveUpOrDown && tableContext.onMoveUpOrDown(rowData, rowIndex, 'up');
        event.stopPropagation();
    }

    const handleMoveDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        tableContext.onMoveUpOrDown && tableContext.onMoveUpOrDown(rowData, rowIndex, 'down');
        event.stopPropagation();
    }

    return (
        <tr
            className={`${isStriped && 'bg-darker'} relative z-0 ${isRowHighlighted ? 'bg-portal-blue-primary bg-opacity-50' : ''} ${showCustomRowColour ? customRowColourWithRange.colour1 : ''}`}
            data-cy="table-row"
            onMouseEnter={(e) => isRowHovered(e, true)}
            onMouseLeave={(e) => isRowHovered(e, false)}
            onClick={(e) => rowClicked(e)}>
            {isSelected !== undefined
                ? (
                    <TableCell
                        borderOptions="border-l border-t border-b"
                        alignmentOptions="w-5 pr-0"
                        columnAttributes={{ active: true }// work-around fragment
                        }>
                        <div id='checkboxId' style={{ width: "3rem", paddingLeft: "0.8rem" }} className="w-content interaction-container" onMouseDown={() => (setCheckBoxClicked(true))}>
                            <input id={`check-${testId}`} type='checkbox' className={`mt-px ${tableContext.alwaysSelectedAndDisabled?.includes(rowData['id']) ? 'cursor-not-allowed' : ''}`} checked={isSelected} disabled={tableContext.alwaysSelectedAndDisabled?.includes(rowData['id'])} />
                        </div>
                    </TableCell>
                )
                : tableContext.onMoveUpOrDown ?
                    <TableCell
                        borderOptions="border-l border-t border-b"
                        alignmentOptions="w-5 pr-0"
                        columnAttributes={{ active: true }// work-around fragment
                        }>
                        <div style={{ width: "3rem", paddingLeft: "0.5rem" }}>
                            <button
                                type="button"
                                className={`mx-1 ${rowIndex === tableContext.tableData.length - 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-500'}`}
                                disabled={rowIndex === tableContext.tableData.length - 1}
                                onClick={(event) => handleMoveDown(event)}>
                                <FontAwesomeIcon icon={faArrowDown} />
                            </button>
                            <button
                                type="button"
                                className={`mx-1 ${(rowIndex === 0) ? 'text-gray-400 cursor-not-allowed ' : 'text-gray-500'}`}
                                disabled={rowIndex === 0} 
                                onClick={(event) => handleMoveUp(event)}>
                                <FontAwesomeIcon icon={faArrowUp} />
                            </button>
                        </div>
                    </TableCell> : null}
            {columnDefinitions
                .filter(column => column.active)
                .map((column, index) => {
                    let borderOptions = 'border';
                    if (index === 0 && isSelected !== undefined) {
                        borderOptions = ' border-t border-b';
                    }
                    else if (index === columnDefinitions.filter(colDef => colDef.active).length - 1) {
                        borderOptions = 'border-l border-t border-b';
                    }

                    // format cell based on content
                    const formattingOptions = column.valueFormatting?.(rowData[column.accessor]);
                    const warningMessage: CellWarning | undefined = column.warning?.(rowData, translate);
                    return <TableCell key={`cell-${index}`}
                        borderOptions={borderOptions}
                        formattingOptions={formattingOptions}
                        columnAttributes={column}>
                        <div className={`flex flex-row ${!!!rowData[column.accessor] && warningMessage ? 'justify-center' : ''}`}>
                            {warningMessage && <Tooltip text={warningMessage.tooltip} direction="left" show={true}>
                                <button onMouseDown={() => tableContext.onShowWarning && tableContext.onShowWarning(warningMessage)} className='mr-2'>
                                    <FontAwesomeIcon style={{ color: 'orange' }} icon={faTriangleExclamation} />
                                </button>
                            </Tooltip>}
                            {rowData[column.accessor]}
                        </div>
                    </TableCell>
                })}
            {rowHovered ? getRowActionsJSX() : null}
        </tr >
    );
}

export default TableRow;
